import clsx from 'clsx'
import { ReactNode } from 'react'

export interface HeaderProps {
  /**
   * Optional heading element type to choose for rendering
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5'
  /**
   * Optional additional class names
   */
  className?: string
  /**
   * The header content.
   */
  children: string | ReactNode
  /**
   * Whether the provided `className` should override the default classnames entirely
   */
  overrideClassName?: boolean
}

export default function Header({
  as = 'h1',
  className = '',
  children,
  overrideClassName = false,
  ...props
}: HeaderProps) {
  const HeadingElement = as

  // A `type-*` typography utility in `className` is an explicit size/weight
  // override; `as` should then only pick the semantic element, not also emit a
  // default `type-*` class. Emitting both lands two conflicting typography
  // utilities on the element, and the winner is decided purely by their order
  // in the compiled CSS. Tailwind v4 sorts these registered utilities
  // alphabetically, so e.g. `type-body-1` + `type-display-2` (from as="h2")
  // resolves to `display-2` — the wrong, oversized one. Skipping the default
  // when the caller supplies their own `type-*` makes the intended class win
  // deterministically. Matches responsive-prefixed overrides too (`lg:type-*`).
  const hasTypeOverride = /(?:^|[\s:])type-/.test(className)

  const headerClassName = overrideClassName
    ? className
    : clsx(
        className,
        !hasTypeOverride &&
          {
            h1: 'type-display-1',
            h2: 'type-display-2',
            h3: 'type-headline-1',
            h4: 'type-headline-3',
            h5: 'type-body-1',
          }[as],
      )

  return (
    <HeadingElement className={headerClassName} {...props}>
      {children}
    </HeadingElement>
  )
}
